import type {
  AppRole,
  CreateAppRoleInput,
  CreateWorkspaceAppCustomPermissionInput,
  UpdateWorkspaceAppInput,
  WorkspaceApp,
  WorkspaceAppCustomPermission
} from 'src/api/facade/types'

import { mutateMockDb, readMockDb } from '../db/state'
import { createId } from '../utils/ids'

import { appendAuditEvent } from './auditRepo'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const mergeAppSettings = (
  base: WorkspaceApp['settings'],
  patch: Record<string, unknown>
): Record<string, unknown> => {
  const b = isRecord(base) ? { ...base } : {}
  const out = { ...b, ...patch }
  const luA = isRecord(b.local_ui) ? b.local_ui : {}
  const luB = isRecord(patch.local_ui) ? patch.local_ui : {}
  if (Object.keys(luA).length > 0 || Object.keys(luB).length > 0) {
    out.local_ui = { ...luA, ...luB }
  }
  return out
}

export const getWorkspaceApp = (appId: string): WorkspaceApp | undefined =>
  readMockDb().apps.find((app) => app.id === appId)

const pushDefaultAppRoles = (db: ReturnType<typeof readMockDb>, appId: string) => {
  db.appRoles.push(
    {
      id: createId('app_role'),
      appId,
      name: 'app_admin',
      description: 'Manage app settings and credentials.',
      permissions: ['apps.manage', 'apps.roles', 'apps.credentials'],
      customPermissionIds: [],
      source: 'workspace',
      system: true
    },
    {
      id: createId('app_role'),
      appId,
      name: 'app_member',
      description: 'Use app and view assigned resources.',
      permissions: ['apps.manage'],
      customPermissionIds: [],
      source: 'workspace',
      system: true
    }
  )
}

/**
 * Ensures default app roles exist for an API-backed app id so mock panels work.
 */
export const ensureAppMockBundle = (
  appId: string,
  workspaceId: number,
  appName: string
): void => {
  if (listAppRoles(appId).length > 0) {
    return
  }

  mutateMockDb((db) => {
    pushDefaultAppRoles(db, appId)
  })

  appendAuditEvent({
    scope: 'app',
    workspaceId,
    appId,
    actor: readMockDb().account.profile.email,
    action: 'app.roles.seeded',
    target: appName,
    severity: 'low'
  })
}

export const updateWorkspaceApp = (
  appId: string,
  patch: UpdateWorkspaceAppInput
): WorkspaceApp | undefined => {
  mutateMockDb((db) => {
    db.apps = db.apps.map((app) => {
      if (app.id !== appId) {
        return app
      }
      const { settings: patchSettings, ...restPatch } = patch
      const next: WorkspaceApp = {
        ...app,
        ...restPatch
      }
      if (patchSettings !== undefined) {
        next.settings = mergeAppSettings(app.settings, patchSettings) as WorkspaceApp['settings']
      }
      return next
    })
  })

  const app = getWorkspaceApp(appId)
  if (app) {
    appendAuditEvent({
      scope: 'app',
      workspaceId: app.workspaceId,
      appId: app.id,
      actor: readMockDb().account.profile.email,
      action: 'app.updated',
      target: app.name,
      severity: 'low'
    })
  }

  return app
}

export const listAppRoles = (appId: string): AppRole[] =>
  readMockDb().appRoles.filter((role) => role.appId === appId)

export const listAppCustomPermissions = (appId: string): WorkspaceAppCustomPermission[] =>
  readMockDb().appCustomPermissions.filter((p) => p.appId === appId)

export const createAppCustomPermission = (
  payload: CreateWorkspaceAppCustomPermissionInput
): WorkspaceAppCustomPermission => {
  const code = payload.codeName.trim().toLowerCase()
  const existing = listAppCustomPermissions(payload.appId)
  if (existing.some((p) => p.codeName.toLowerCase() === code)) {
    throw new Error('duplicate_app_permission_code')
  }

  let created: WorkspaceAppCustomPermission | undefined

  mutateMockDb((db) => {
    created = {
      id: createId('app_perm'),
      appId: payload.appId,
      codeName: code,
      displayName: payload.displayName?.trim() || undefined,
      description: payload.description?.trim() || undefined,
      createdAt: new Date().toISOString()
    }
    db.appCustomPermissions.unshift(created)
  })

  const row = created as WorkspaceAppCustomPermission
  const app = getWorkspaceApp(payload.appId)

  appendAuditEvent({
    scope: 'app',
    workspaceId: app?.workspaceId,
    appId: payload.appId,
    actor: readMockDb().account.profile.email,
    action: 'app.permission.created',
    target: row.codeName,
    severity: 'medium'
  })

  return row
}

export const deleteAppCustomPermission = (
  appId: string,
  permissionId: string
): WorkspaceAppCustomPermission[] => {
  let code = permissionId

  mutateMockDb((db) => {
    const row = db.appCustomPermissions.find(
      (p) => p.appId === appId && p.id === permissionId
    )
    if (!row) {
      return
    }
    code = row.codeName

    db.appCustomPermissions = db.appCustomPermissions.filter(
      (p) => !(p.appId === appId && p.id === permissionId)
    )

    db.appRoles = db.appRoles.map((role) =>
      role.appId === appId
        ? {
            ...role,
            customPermissionIds: role.customPermissionIds.filter((id) => id !== permissionId)
          }
        : role
    )
  })

  const app = getWorkspaceApp(appId)

  appendAuditEvent({
    scope: 'app',
    workspaceId: app?.workspaceId,
    appId,
    actor: readMockDb().account.profile.email,
    action: 'app.permission.deleted',
    target: code,
    severity: 'high'
  })

  return listAppCustomPermissions(appId)
}

export const createAppRole = (payload: CreateAppRoleInput): AppRole => {
  let created: AppRole | undefined

  mutateMockDb((db) => {
    const allowed = new Set(
      db.appCustomPermissions.filter((p) => p.appId === payload.appId).map((p) => p.id)
    )
    const customIds = (payload.customPermissionIds ?? []).filter((id) => allowed.has(id))

    created = {
      id: createId('app_role'),
      appId: payload.appId,
      name: payload.name,
      description: payload.description,
      permissions: payload.permissions,
      customPermissionIds: customIds,
      source: payload.source ?? 'custom',
      system: false
    }
    db.appRoles.unshift(created)
  })

  const role = created as AppRole
  const app = getWorkspaceApp(payload.appId)

  appendAuditEvent({
    scope: 'app',
    workspaceId: app?.workspaceId,
    appId: payload.appId,
    actor: readMockDb().account.profile.email,
    action: 'app.role.created',
    target: role.name,
    severity: 'medium'
  })

  return role
}

export const updateAppRole = (
  appId: string,
  roleId: string,
  patch: Partial<Pick<AppRole, 'name' | 'description' | 'permissions' | 'customPermissionIds'>>
): AppRole | undefined => {
  mutateMockDb((db) => {
    const allowed = new Set(
      db.appCustomPermissions.filter((p) => p.appId === appId).map((p) => p.id)
    )

    db.appRoles = db.appRoles.map((role) => {
      if (!(role.appId === appId && role.id === roleId)) {
        return role
      }
      const merged = { ...role, ...patch }
      if (patch.customPermissionIds != null) {
        merged.customPermissionIds = patch.customPermissionIds.filter((id) => allowed.has(id))
      }
      return merged
    })
  })

  const role = listAppRoles(appId).find((item) => item.id === roleId)
  const app = getWorkspaceApp(appId)

  if (role) {
    appendAuditEvent({
      scope: 'app',
      workspaceId: app?.workspaceId,
      appId,
      actor: readMockDb().account.profile.email,
      action: 'app.role.updated',
      target: role.name,
      severity: 'medium'
    })
  }

  return role
}

export const deleteAppRole = (appId: string, roleId: string): AppRole[] => {
  let roleName = roleId

  mutateMockDb((db) => {
    const target = db.appRoles.find(
      (role) => role.appId === appId && role.id === roleId
    )
    if (target?.system) {
      return
    }

    roleName = target?.name ?? roleId
    db.appRoles = db.appRoles.filter(
      (role) => !(role.appId === appId && role.id === roleId)
    )
  })

  const app = getWorkspaceApp(appId)

  appendAuditEvent({
    scope: 'app',
    workspaceId: app?.workspaceId,
    appId,
    actor: readMockDb().account.profile.email,
    action: 'app.role.deleted',
    target: roleName,
    severity: 'high'
  })

  return listAppRoles(appId)
}
